import { HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';

import { HttpError, HttpErrorBadRequest, HttpMethod, HttpStatus, RequestError, RequestErrorDetails } from '../../models';

interface HttpErrorBaseOldProps {
  readonly customErrors: unknown;
  readonly headers: HttpHeaders;
  readonly statusCode: number;
  readonly traceparent: string;
  readonly url: string | null;
}

type ErrorCode =
  | 'AccessDeniedErrorCode'
  | 'CategoryUnavailable'
  | 'CmekNotActivated'
  | 'DocumentNameExist'
  | 'FileDownloadException'
  | 'FileSizeIsTooBig'
  | 'FileWithoutIndexes'
  | 'IncorrectFileContent'
  | 'IncorrectFileExtension'
  | 'IncorrectFileName'
  | 'QuestionAnswered'
  | 'QuestionPostUnavailable'
  | 'StorageLimit';

const ERROR_TRANSLATIONS: Record<ErrorCode, string> = {
  AccessDeniedErrorCode: 'common.ERROR.insufficient_permissions',
  CategoryUnavailable: 'common.ERROR.insufficient_permissions',
  CmekNotActivated: 'documents.Upload.Error.CMEK',
  DocumentNameExist: 'common.ERROR.name_is_used',
  FileDownloadException: 'documents.Toaster.TEXT.File_couldn\'t_be_downloaded',
  FileSizeIsTooBig: 'common.Error.Failed_image',
  FileWithoutIndexes: 'documents.ImportStructure.Error.No_index_found_in_file',
  IncorrectFileContent: 'common.TEXT.Import_failed',
  IncorrectFileExtension: 'common.Error.Unsupported_file',
  IncorrectFileName: 'Incorrect file name',
  QuestionAnswered: 'common.ERROR.insufficient_permissions',
  QuestionPostUnavailable: 'common.ERROR.insufficient_permissions',
  StorageLimit: 'documents.Upload.Error.Storage_limit',
};
const TRANSLATED_ERROR_DETAILS = new Set<string>(Object.values(ERROR_TRANSLATIONS));

const HTTP_ERROR_STATUSES: Record<number, HttpStatus> = {
  0: 'No Response',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  409: 'Conflict',
  503: 'Service Unavailable',
};

enum HttpStatusNoConnectionCode {
  NoConnection = 0,
}

export function isTranslatedErrorDetail(detail: string): boolean {
  return TRANSLATED_ERROR_DETAILS.has(detail);
}

export function normalizeHttpError(method: string, response: HttpErrorResponse): HttpError | RequestError {
  if ((response.error as { errorCode: string; })?.errorCode === undefined) {
    return normalizeHttpErrorOld(method as HttpError['method'], response);
  }

  const error = response.error as {
    readonly detail: string;
    readonly errorCode: number;
    readonly errors?: RequestErrorDetails;
    readonly traceId: string;
  };

  return {
    detail: ERROR_TRANSLATIONS[error.detail as ErrorCode] ?? error.detail,
    errorCode: error.errorCode,
    errors: error.errors,
    headers: response.headers,
    method: method as HttpMethod,
    responseError: response.error,
    status: HTTP_ERROR_STATUSES[response.status] ?? 'Internal Server Error',
    statusCode: response.status,
    traceparent: error.traceId,
    url: response.url,
  } satisfies RequestError;
}

function normalizeHttpErrorOld(method: HttpMethod, response: HttpErrorResponse): HttpError {
  let customErrors = '';

  // TODO Remove this f.ck code when  BE implement single approach for ErrorResponse
  if (response.error) {
    if (typeof response.error === 'string') {
      customErrors = response.error;
    } else if ('errors' in response.error) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
      customErrors = response.error['errors'];
    }
  }

  const props: HttpErrorBaseOldProps = {
    customErrors,
    headers: response.headers,
    statusCode: response.status,
    url: response.url,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
    traceparent: response.error?.['traceparent'] ?? response.error?.['traceId'] ?? '',
  };

  switch (response.status as HttpStatusNoConnectionCode | HttpStatusCode) {
    case HttpStatusNoConnectionCode.NoConnection: {
      return { ...props, method, status: 'No Response' };
    }
    case HttpStatusCode.BadRequest: {
      return normalizeHttpErrorOldBadRequest(method, response, props);
    }
    case HttpStatusCode.Unauthorized: {
      return { ...props, method, status: 'Unauthorized' };
    }
    case HttpStatusCode.Forbidden: {
      return {
        ...props,
        error: response.error,
        message: ERROR_TRANSLATIONS[(response.error as { message: ErrorCode; })?.message] ?? undefined,
        method,
        status: 'Forbidden',
      };
    }
    case HttpStatusCode.NotFound: {
      return { ...props, method, status: 'Not Found' };
    }
    case HttpStatusCode.Conflict: {
      return {
        ...props,
        error: response.error,
        method,
        status: 'Conflict',
      };
    }
    case HttpStatusCode.ServiceUnavailable: {
      return { ...props, method, status: 'Service Unavailable' };
    }
    default: {
      return { ...props, method, status: 'Internal Server Error' };
    }
  }
}

function normalizeHttpErrorOldBadRequest(
  method: HttpError['method'],
  response: HttpErrorResponse,
  props: HttpErrorBaseOldProps,
): HttpErrorBadRequest {
  const defaultGeneralMessage = 'common.Error.Something_went_wrong';
  const logIncorrectFormat = (): void => {
    console.error(`Incorrect response format: ${method}/Bad Request/${JSON.stringify(response.error)}`);
  };

  if (response.error == null || typeof response.error !== 'object') {
    logIncorrectFormat();

    return {
      ...props,
      message: defaultGeneralMessage,
      method,
      status: 'Bad Request',
      type: 'General',
    };
  }

  const error = response.error as {
    errors?: Record<string, ErrorCode>;
    message?: ErrorCode;
    type?: string;
  };
  const type: string = error.type ?? 'GENERAL';

  if (type === 'GENERAL') {
    if (!error.message) {
      logIncorrectFormat();
    }

    return {
      ...props,
      error,
      message: ERROR_TRANSLATIONS[error.message!] ?? error.message ?? defaultGeneralMessage,
      method,
      status: 'Bad Request',
      type: 'General',
    };
  }

  if (type === 'DETAILED') {
    if (typeof error.errors !== 'object' || Object.keys(error.errors).length === 0) {
      logIncorrectFormat();
    }

    const errors: Record<string, string> = {};

    for (const key in error.errors) {
      errors[key] = ERROR_TRANSLATIONS[error.errors[key]] ?? error.errors[key];
    }

    return {
      ...props,
      method,
      errors,
      status: 'Bad Request',
      type: 'Detailed',
    };
  }

  logIncorrectFormat();

  return {
    ...props,
    message: defaultGeneralMessage,
    method,
    status: 'Bad Request',
    type: 'General',
  };
}
